import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { EventOrigin } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useDispatchSelectionBasket } from "contexts/selectionBasket";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import { selectDefaultVisibility, selectMainObject, selectPreIsolationVisibility } from "features/render";

import { createBulkActions } from "../objectTree/utils/bulkActions";
import { ContextMenuPopup, ContextMenuState } from "./ContextMenuPopup";

interface UseBulkActionsContextMenuOptions {
    origin?: EventOrigin;
}

/**
 * Shared hook for bulk-action context menus.
 *
 * Manages the menu state (position + actions), keeps the actions fresh while
 * the menu is open (e.g. when async selection completes after the menu is
 * already rendered), and exposes the `openContextMenu` / `closeContextMenu`
 * callbacks plus a ready-to-render `<ContextMenuPopup>` component.
 */
export function useBulkActionsContextMenu({ origin }: UseBulkActionsContextMenuOptions = {}) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const mainObject = useAppSelector(selectMainObject);
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const highlightedSet = useHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHidden = useDispatchHidden();
    const hidden = useHidden();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const { flyToSelected } = useFlyToSelected();

    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

    const closeContextMenu = useCallback(() => {
        setContextMenu(null);
    }, []);

    const buildActions = useCallback(
        (onActionComplete: () => void) =>
            createBulkActions(highlightedSet.idArr.length, highlightedSet.idArr, {
                t,
                dispatch,
                dispatchHighlighted,
                dispatchHidden,
                dispatchSelectionBasket,
                defaultVisibility,
                preIsolationVisibility,
                flyToSelected,
                currentMainObject: mainObject,
                onActionComplete,
                hiddenIds: hidden.ids,
                lastHiddenIds: hidden.lastHiddenIds,
                origin,
            }),
        [
            highlightedSet.idArr,
            t,
            dispatch,
            dispatchHighlighted,
            dispatchHidden,
            dispatchSelectionBasket,
            defaultVisibility,
            preIsolationVisibility,
            flyToSelected,
            mainObject,
            hidden.ids,
            hidden.lastHiddenIds,
            origin,
        ],
    );

    const openContextMenu = useCallback(
        (mouseX: number, mouseY: number) => {
            const actions = buildActions(closeContextMenu);
            setContextMenu({ mouseX, mouseY, actions });
        },
        [buildActions, closeContextMenu],
    );

    // Update context menu actions when selection changes while menu is open.
    // This handles the case where right-click triggers async selection that completes
    // after the menu is already displayed, ensuring menu items are enabled correctly.
    useEffect(() => {
        setContextMenu((openMenu) => {
            if (!openMenu) {
                return null;
            }

            const updatedActions = buildActions(closeContextMenu);
            return { ...openMenu, actions: updatedActions };
        });
    }, [buildActions, closeContextMenu, highlightedSet.idArr]);

    const contextMenuNode = useMemo(
        () => <ContextMenuPopup contextMenu={contextMenu} onClose={closeContextMenu} />,
        [contextMenu, closeContextMenu],
    );

    return {
        contextMenu,
        openContextMenu,
        closeContextMenu,
        buildActions,
        contextMenuNode,
    };
}
